<script>
  import { onMount, tick } from "svelte";
  import { progress, save } from "../lib/state.svelte.js";
  import { ERAS, ERA_BY_ID, FUTURE_LAP_MA } from "../../data/eras.js";
  import { eraForEvo, nextMilestone, eraWaypoints } from "../../data/journeyEngine.js";
  import { sceneFor } from "../../data/scenes.js";
  import { organismInner, FORM_NAMES } from "../../data/organism.js";
  import { fmtEra, fmtFuture, fmtEvo } from "../lib/format.js";
  import { problemsToGo } from "../lib/economy.js";
  import { journeyStars, trailMarks, pointsAlong } from "../lib/sceneRender.js";
  import { panZoom } from "../lib/zoom.js";
  import { playSound } from "../lib/audio.js";
  import { journeyUi } from "../lib/rewards.svelte.js";

  const here = $derived(eraForEvo(progress.evo));
  let tab = $state(null);
  const tabIndex = $derived(tab ?? here.index);
  const era = $derived(ERAS[tabIndex]);
  const scene = $derived(sceneFor(era.id));

  /** そのタブの踏破率。過去=1、未来=0、現在=実際の進捗。 */
  const frac = $derived(
    tabIndex < here.index ? 1 : tabIndex === here.index ? here.fraction : 0
  );
  const locked = $derived(tabIndex > here.index);
  /** 2つ先から「？？？」で隠す（1つ先は予告として見せる） */
  const hidden = $derived(tabIndex >= here.index + 2);

  const stars = $derived(
    scene.stars
      ? journeyStars(scene.stars, tabIndex * 100, scene.vbW, scene.skyH ?? scene.vbH * 0.55)
      : []
  );
  const waypoints = $derived(eraWaypoints(era));
  const next = $derived(nextMilestone(progress.evo));

  const eraText = $derived(here.isFuture ? fmtFuture(here.futureMa) : fmtEra(here.ma));
  const remainText = $derived.by(() => {
    if (here.isFuture) return `${here.lap} 周目 — 進化はまだ続く`;
    const n = problemsToGo(here.era.endEvo);
    return `進行 <b>${Math.round(here.fraction * 100)}%</b> ／ 次の時代まで あと <b>${n}</b> 問`;
  });
  const nextToGo = $derived(next ? problemsToGo(next.at) : 0);

  // ── 道の上の座標を測る ────────────────────────────────
  let pathEl = $state(null);
  let svgEl = $state(null);
  let wpPoints = $state([]);
  let vehPos = $state({ x: 0, y: 0 });
  let trail = $state([]);
  let pathLen = $state(0);

  async function measure() {
    await tick();
    if (!pathEl) return;
    try {
      pathLen = pathEl.getTotalLength();
    } catch (e) {
      return;
    }
    wpPoints = pointsAlong(
      pathEl,
      waypoints.map((w) => w.pos)
    );
    const p = pointsAlong(pathEl, [frac])[0];
    vehPos = p;
    trail = hidden ? [] : trailMarks(pathEl, frac, scene.trail || "dust");
  }

  // シーンか進捗が変わったら測り直す
  $effect(() => {
    // 依存を明示的に読む
    void era.id;
    void frac;
    void hidden;
    measure();
  });

  // ── ストーリーポップアップ ────────────────────────────
  let storyEra = $state(null);
  let storyShow = $state(false);

  onMount(flushStory);

  $effect(() => {
    // 昇格予約が入ったとき、および到達時代が変わったときに見直す
    void journeyUi.pendingStory;
    void here.era.id;
    flushStory();
  });

  async function flushStory() {
    if (storyEra) return; // すでに表示中
    // 昇格直後に予約されたものを優先。無ければ、いま居る時代の
    // ストーリーが未読なら出す（旅タブを開く前にリロードしても見られる）。
    const id =
      journeyUi.pendingStory ||
      (!here.era.story || progress.storiesSeen.includes(here.era.id) ? null : here.era.id);
    if (!id) return;
    journeyUi.pendingStory = null;
    storyEra = ERA_BY_ID[id];
    tab = ERAS.findIndex((e) => e.id === id);
    await tick();
    requestAnimationFrame(() => (storyShow = true));
  }

  function closeStory() {
    // 実際に見せたここではじめて既読にする
    if (storyEra && !progress.storiesSeen.includes(storyEra.id)) {
      progress.storiesSeen.push(storyEra.id);
    }
    storyShow = false;
    setTimeout(() => {
      storyEra = null;
      journeyUi.morphFrom = null;
    }, 350);
    save();
  }

  function selectTab(i) {
    tab = i;
    playSound("click");
  }

  function zoom(kind) {
    if (svgEl && svgEl.__zoom) svgEl.__zoom[kind]();
  }
</script>

<section>
  <div class="card" id="journeyCard">
    <div class="jscene-wrap">
      <div class="jscene" class:mystery={hidden} style={hidden ? "" : `background:${scene.bg}`}>
        <svg
          bind:this={svgEl}
          class="jscene-svg"
          viewBox="0 0 {scene.vbW} {scene.vbH}"
          preserveAspectRatio="xMidYMid slice"
          use:panZoom={{ vbW: scene.vbW, vbH: scene.vbH, sceneId: era.id, focus: null }}
          role="img"
          aria-label="{era.name}の風景"
        >
          <!-- 背景 -->
          {@html scene.svg}

          <!-- 星 -->
          {#each stars as s, i (i)}
            <circle
              class="jstar"
              cx={s.x}
              cy={s.y}
              r={s.r}
              fill="#fff"
              opacity={s.op}
              style="animation-delay:{s.delay}s"
            />
          {/each}

          <!-- 道：グロー下地／未踏の点線／踏破済み -->
          <path
            d={scene.route}
            fill="none"
            stroke={scene.routeStroke}
            stroke-width="8"
            stroke-linecap="round"
            opacity="0.14"
          />
          <path
            class="jroute-future"
            d={scene.route}
            fill="none"
            stroke={scene.routeDim}
            stroke-width="3"
            stroke-dasharray="1 7"
          />
          <path
            bind:this={pathEl}
            id="jrouteTravel"
            d={scene.route}
            fill="none"
            stroke={scene.routeStroke}
            stroke-width="3.4"
            stroke-linecap="round"
            style="stroke-dasharray:{pathLen}; stroke-dashoffset:{pathLen * (1 - frac)}"
          />

          <!-- 通った跡 -->
          <g class="jtrail">
            {#each trail as m, i (i)}
              {#if m.kind === "foot"}
                <ellipse
                  cx={m.x}
                  cy={m.y}
                  rx="1.9"
                  ry="1.1"
                  transform="rotate({m.rot} {m.x} {m.y})"
                  fill={scene.routeStroke}
                  opacity={m.op}
                />
              {:else}
                <circle cx={m.x} cy={m.y} r={m.r} fill={scene.routeStroke} opacity={m.op} />
              {/if}
            {/each}
          </g>

          <!-- チェックポイント（進化の重大イベント） -->
          {#if !hidden}
            {#each waypoints as w, i (w.label)}
              {@const pt = wpPoints[i] || { x: 0, y: 0 }}
              {@const reached = progress.milestonesReached.includes(`${era.id}:${w.label}`)}
              <g opacity={reached ? 1 : 0.55}>
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r={reached ? 3.4 : 2.4}
                  fill={reached ? scene.routeStroke : "#fff"}
                  stroke={scene.routeStroke}
                  stroke-width="1.4"
                />
                <text
                  x={pt.x}
                  y={pt.y - 7}
                  font-size="7.5"
                  text-anchor="middle"
                  fill={scene.labelColor}
                  style="paint-order:stroke;stroke:{scene.labelHalo};stroke-width:2.4px;stroke-linejoin:round;font-weight:700"
                >
                  {reached ? w.em : "？"}
                  {reached ? w.label : ""}
                </text>
              </g>
            {/each}
          {/if}

          <!-- 旅人 -->
          {#if !hidden}
            <g
              id="jvehicle"
              class:arrived={frac >= 1 && !locked}
              transform="translate({vehPos.x},{vehPos.y})"
              opacity={locked ? 0.35 : 1}
            >
              <ellipse cx="0" cy="11" rx="7" ry="2.2" fill="rgba(0,0,0,.3)" />
              <g class="jveh-bob">
                <g transform="translate(-13,-13) scale(0.26)">
                  {@html organismInner(era.form, progress.organism)}
                </g>
              </g>
            </g>
          {/if}
        </svg>

        <!-- 現在地の情報 -->
        <div class="journey-overlay">
          <div class="journey-headline">🧬 <b>{eraText}</b></div>
          <div class="journey-stage-now">
            {here.era.em} <b>{here.era.name}</b> ／ {@html remainText}
          </div>
          {#if next}
            <div class="journey-next">
              🚩 次の節目：{next.em} {next.label}（あと {nextToGo} 問）
            </div>
          {/if}
        </div>

        {#if hidden}
          <div class="jscene-veil">
            <span class="jscene-veil-q">？</span>
            <span class="jscene-veil-cap">まだ見ぬ時代</span>
          </div>
        {:else}
          <div class="jscene-zoom">
            <button onclick={() => zoom("in")} aria-label="拡大">＋</button>
            <button onclick={() => zoom("out")} aria-label="縮小">－</button>
            <button onclick={() => zoom("reset")} aria-label="全体表示">⟲</button>
          </div>
        {/if}

        <div class="jscene-label" class:locked>
          {hidden ? "❓ ？？？" : `${era.em} ${era.name}`} —
          {#if hidden}
            未知の時代
          {:else if locked}
            🔒 まだ到達していない（予告）
          {:else if frac >= 1}
            ✅ 通過済み
          {:else}
            🚩 進行中 {Math.round(frac * 100)}%
          {/if}
        </div>

      </div>
    </div>

    <!-- 時代タブ -->
    <div class="journey-tabs">
      {#each ERAS as e, i (e.id)}
        {@const st = i < here.index ? "done" : i === here.index ? "current" : "locked"}
        {@const veiled = i >= here.index + 2}
        <button
          class="jtab {st}"
          class:sel={i === tabIndex}
          class:mystery={veiled}
          onclick={() => selectTab(i)}
        >
          <span class="jtab-em">{veiled ? "❓" : e.em}</span>
          <span class="jtab-name">{veiled ? "？？？" : e.name}</span>
          <span class="jtab-mark">{st === "done" ? "✓" : st === "current" ? "🚩" : "🔒"}</span>
        </button>
      {/each}
    </div>
  </div>

  <div class="card">
    <h3>{here.era.em} {here.era.name}</h3>
    <p class="muted" style="margin-top:0">{here.era.desc}</p>
    <div class="tagrow">
      <span class="tag">いまの姿：{FORM_NAMES[here.era.form]}</span>
      <span class="tag"
        >{here.isFuture
          ? `${FUTURE_LAP_MA * (here.lap - 1) * 100} 万年先`
          : `${fmtEra(here.era.fromMa)} 〜 ${fmtEra(here.era.toMa)}`}</span
      >
      <span class="tag">進化ポイント {fmtEvo(progress.evo)}</span>
    </div>
  </div>

  <!-- ステージ昇格のストーリー。
       画面いっぱいに出す（position:fixed）ので、.card の外に置く。
       .view-enter .card は一瞬 transform が乗り、その間 fixed の基準が
       カードになってしまうため、カードの中に入れてはいけない。 -->
  {#if storyEra}
    <div
      class="story-pop"
      class:show={storyShow}
      onclick={closeStory}
      onkeydown={(e) => e.key === "Escape" && closeStory()}
      role="button"
      tabindex="-1"
    >
      <div
        class="story-card"
        onclick={(e) => e.stopPropagation()}
        onkeydown={() => {}}
        role="presentation"
      >
        <div class="story-em">{storyEra.em}</div>
        <div class="story-title">{storyEra.story.title}</div>
        <div class="story-era">
          {storyEra.name} ／ {fmtEra(storyEra.fromMa)} 〜 {storyEra.toMa === null
            ? "未来"
            : fmtEra(storyEra.toMa)}
        </div>
        <div class="story-lines">
          {#each storyEra.story.lines as line}
            <p>{line}</p>
          {/each}
        </div>

        {#if journeyUi.morphFrom}
          <div class="story-morph">
            <svg viewBox="0 0 100 100" aria-hidden="true">
              {@html organismInner(journeyUi.morphFrom, progress.organism)}
            </svg>
            <span>→</span>
            <svg viewBox="0 0 100 100" aria-hidden="true">
              {@html organismInner(storyEra.form, progress.organism)}
            </svg>
            <span class="tiny">{FORM_NAMES[storyEra.form]} になった</span>
          </div>
        {/if}

        <button class="btn submit" style="margin-top:16px" onclick={closeStory}>
          {storyEra.story.cta}
        </button>
      </div>
    </div>
  {/if}
</section>
